import React from 'react';

/**
 * Segmented view switch (ST-00097) — one style for the Groupings/Products and
 * Current Tickets/Amendment History toggles that used to be styled per page.
 *
 * Segments share the width equally, so it fills a phone-width toolbar row and
 * sizes to content in a wide header. Touch height is 40px inside a 44px track
 * on coarse pointers, compact on fine pointers.
 *
 * options: [{ value, label, shortLabel?, icon?: Component, badge?: ReactNode }]
 *   shortLabel replaces label on very narrow screens (< 400px) so a count badge
 *   still fits beside it.
 */
const SegmentedControl = ({ options, value, onChange, ariaLabel, className = '' }) => (
  <div
    role="tablist"
    aria-label={ariaLabel}
    className={`grid grid-flow-col auto-cols-fr items-stretch gap-1 p-1 bg-surface-sunken ring-1 ring-inset ring-slate-200 rounded-lg ${className}`}
  >
    {options.map(({ value: optionValue, label, shortLabel, icon: Icon, badge }) => {
      const selected = optionValue === value;
      return (
        <button
          key={optionValue}
          type="button"
          role="tab"
          aria-selected={selected}
          onClick={() => onChange(optionValue)}
          className={`inline-flex items-center justify-center gap-1.5 min-w-0 px-3 h-9 [@media(pointer:coarse)]:h-10 rounded-md text-sm font-medium whitespace-nowrap transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
            selected
              ? 'bg-surface-raised text-slate-900 shadow-sm ring-1 ring-slate-200 dark:ring-slate-300'
              : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          {Icon && <Icon className="w-3 h-3 flex-shrink-0" aria-hidden="true" />}
          {shortLabel ? (
            <>
              <span className="min-[400px]:hidden">{shortLabel}</span>
              <span className="hidden min-[400px]:inline">{label}</span>
            </>
          ) : (
            <span>{label}</span>
          )}
          {badge != null && (
            <span
              className={`rounded-full px-1.5 py-px text-[11px] leading-4 tabular-nums ${
                selected ? 'bg-primary-500/15 text-primary-700' : 'bg-slate-200 text-slate-600'
              }`}
            >
              {badge}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

export default SegmentedControl;
